import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useParams } from "@tanstack/react-router";
import { useState, type ReactElement, type ReactNode } from "react";
import {
  ConfirmDialog,
  ContentHeader,
  Field,
  Modal,
  QtyInput,
  QueryStatus,
  StatusBadge,
  useToast,
} from "@cx/ui";
import type {
  ProductionOrderView,
  ProductionRunView,
  ProductionStatusRef,
  PurchaseOrderHistoryItem,
} from "@cx/contracts";
import { formatBRL, formatDateBR, formatPct, formatQty, todayInSaoPaulo } from "@cx/domain";
import { can, type Permissions } from "@cx/db";
import { errorMessage } from "../../errors";
import { useSession } from "../../session";
import { purchaseHistoryLabel } from "../purchase-orders/labels";
import { LotFields } from "../stock/LotFields";
import { emptyLotDraft, lotDraftProblem, lotPayload } from "../stock/labels";
import { PRODUCTION_PROGRESS_LABELS, RUN_SOURCE_LABELS } from "./labels";

const dateTimeBR = (iso: string) =>
  new Date(iso).toLocaleString("pt-BR", {
    timeZone: "America/Sao_Paulo",
    dateStyle: "short",
    timeStyle: "short",
  });

/**
 * Ordem de produção (RF-SUP-05): produto, quantidades (planejada, produzida, saldo), insumos com necessário/consumido e
 * disponível, apontamentos (estornáveis) com custo, situação com histórico; "Apontar produção" e "Finalizar".
 */
export function ProductionOrderViewPage(): ReactElement {
  const { id } = useParams({ from: "/suprimentos/ordens-de-producao/$id" });
  const { companyId, me, api } = useSession();
  const perms = me.permissions as Permissions;
  const qc = useQueryClient();
  const toast = useToast();
  const order = useQuery({
    queryKey: ["production-orders", companyId, "view", id],
    refetchOnMount: "always",
    queryFn: () => api<ProductionOrderView>(`/production-orders/${id}`),
  });
  const history = useQuery({
    queryKey: ["production-orders", companyId, "history", id],
    refetchOnMount: "always",
    queryFn: () => api<{ data: PurchaseOrderHistoryItem[] }>(`/production-orders/${id}/status-history`),
  });
  const refresh = () => {
    qc.invalidateQueries({ queryKey: ["production-orders", companyId] });
    qc.invalidateQueries({ queryKey: ["stock-balances", companyId] });
    qc.invalidateQueries({ queryKey: ["stock-movements", companyId] });
    qc.invalidateQueries({ queryKey: ["stock-lots", companyId] });
  };
  const onError = (e: Error) => {
    toast("danger", errorMessage(e));
    refresh();
  };
  const status = useMutation({
    mutationFn: (to: ProductionStatusRef) =>
      api(`/production-orders/${id}/status`, {
        method: "POST",
        headers: { "Idempotency-Key": crypto.randomUUID() },
        body: JSON.stringify({ toStatusId: to.id, expectedFromStatusId: order.data!.status.id }),
      }),
    onSuccess: (_d, to) => {
      toast("success", `Situação alterada para ${to.label}`);
      refresh();
    },
    onError,
  });
  const reverse = useMutation({
    mutationFn: (v: { run: ProductionRunView; reason: string }) =>
      api<ProductionOrderView>(`/production-orders/${id}/runs/${v.run.id}/reverse`, {
        method: "POST",
        headers: { "Idempotency-Key": crypto.randomUUID() },
        body: JSON.stringify({ reason: v.reason.trim() || null }),
      }),
    onSuccess: (_d, v) => {
      toast("success", `Apontamento ${v.run.number} estornado`);
      refresh();
    },
    onError,
  });
  const [dialog, setDialog] = useState<"run" | "finish" | null>(null);
  const [canceling, setCanceling] = useState<ProductionStatusRef | null>(null);
  const [reversing, setReversing] = useState<ProductionRunView | null>(null);
  const [reason, setReason] = useState("");

  const o = order.data;
  const canUpdate = can(perms, "production_orders", "update");
  const canStock = can(perms, "stock", "create");
  const busy = status.isPending || reverse.isPending;
  const cancelTo = o?.nextStatuses.find((s) => s.baseState === "canceled") ?? null;
  const open = !!o && (o.status.baseState === "open" || o.status.baseState === "in_progress");
  return (
    <>
      <ContentHeader
        title={o ? `Ordem de produção nº ${o.number}` : "Ordem de produção"}
        breadcrumb={["Suprimentos", "Ordens de produção", o ? String(o.number) : ""]}
        actions={
          o && (
            <>
              <Link to="/suprimentos/ordens-de-producao" className="btn btn-outline-secondary">
                Voltar
              </Link>
              {canUpdate && canStock && o.canProduce && (
                <button type="button" className="btn btn-primary" onClick={() => setDialog("run")}>
                  Apontar produção
                </button>
              )}
              {canUpdate && canStock && open && (
                <button type="button" className="btn btn-outline-primary" onClick={() => setDialog("finish")}>
                  Finalizar
                </button>
              )}
              {canUpdate && !o.lockedReason && (
                <Link
                  to="/suprimentos/ordens-de-producao/$id/editar"
                  params={{ id: o.id }}
                  className="btn btn-outline-primary"
                >
                  Editar
                </Link>
              )}
              {canUpdate && cancelTo && (
                <button
                  type="button"
                  className="btn btn-outline-danger"
                  disabled={busy}
                  onClick={() => setCanceling(cancelTo)}
                >
                  Cancelar ordem
                </button>
              )}
            </>
          )
        }
      />
      <QueryStatus loading={order.isLoading} error={order.error} />
      {o && (
        <div className="row">
          <div className="col-xl-9">
            <div className="box card p-3 mb-3">
              <div className="d-flex flex-wrap align-items-center gap-2 mb-3">
                <span>Situação:</span>
                <StatusBadge tone={o.status.color}>{o.status.label}</StatusBadge>
                <span className="text-muted small" data-testid="op-progress">
                  {PRODUCTION_PROGRESS_LABELS[o.progress]}
                </span>
                {canUpdate && o.nextStatuses.some((s) => s.baseState !== "canceled") && (
                  <div className="d-flex flex-wrap gap-1 ms-md-3" role="group" aria-label="Alterar situação">
                    {o.nextStatuses
                      .filter((s) => s.baseState !== "canceled")
                      .map((s) => (
                        <button
                          key={s.id}
                          type="button"
                          className="btn btn-sm btn-outline-secondary"
                          disabled={busy}
                          onClick={() => status.mutate(s)}
                        >
                          Marcar como {s.label}
                        </button>
                      ))}
                  </div>
                )}
              </div>
              <dl className="row mb-0">
                <Info label="Produto">
                  {o.product.name}
                  {o.product.sku && <span className="text-muted ms-2">{o.product.sku}</span>}
                </Info>
                <Info label="Quantidade">
                  <span data-testid="op-quantities">
                    {formatQty(o.quantityQ4)} {o.product.unit} · produzido {formatQty(o.producedQ4)}
                    {o.closedQ4 > 0 && ` · encerrado ${formatQty(o.closedQ4)}`} · saldo{" "}
                    {formatQty(o.remainingQ4)}
                  </span>
                </Info>
                <Info label="Depósito dos insumos">{o.sourceWarehouse.name}</Info>
                <Info label="Depósito do acabado">{o.targetWarehouse.name}</Info>
                <Info label="Responsável">{o.responsible}</Info>
                <Info label="Previsão">
                  {o.plannedStart || o.plannedEnd
                    ? `${o.plannedStart ? formatDateBR(o.plannedStart) : "—"} a ${
                        o.plannedEnd ? formatDateBR(o.plannedEnd) : "—"
                      }`
                    : null}
                </Info>
                <Info label="Início / término">
                  {o.startedAt || o.finishedAt
                    ? `${o.startedAt ? dateTimeBR(o.startedAt) : "—"} / ${
                        o.finishedAt ? dateTimeBR(o.finishedAt) : "—"
                      }`
                    : null}
                </Info>
                {o.product.batchControl && (
                  <Info label="Lote">
                    {o.lot
                      ? `${o.lot.code}${o.lot.expiresAt ? ` · validade ${formatDateBR(o.lot.expiresAt)}` : ""}`
                      : "Informe em cada apontamento"}
                  </Info>
                )}
              </dl>
              {o.notes && (
                <p className="mb-0 mt-2" style={{ whiteSpace: "pre-wrap" }}>
                  {o.notes}
                </p>
              )}
            </div>
            <div className="box card mb-3">
              <div
                className="table-responsive"
                tabIndex={0}
                role="region"
                aria-label="Insumos da ordem de produção (rolagem)"
              >
                <table className="table align-middle mb-0" aria-label="Insumos da ordem de produção">
                  <thead>
                    <tr>
                      <th scope="col">Insumo</th>
                      <th scope="col">Un</th>
                      <th scope="col" className="text-end">
                        Por unidade
                      </th>
                      <th scope="col" className="text-end">
                        Perda
                      </th>
                      <th scope="col" className="text-end">
                        Necessário
                      </th>
                      <th scope="col" className="text-end">
                        Consumido
                      </th>
                      <th scope="col" className="text-end">
                        Disponível
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {o.items.map((it) => {
                      const short =
                        it.stockControl &&
                        it.availableQ4 !== null &&
                        it.availableQ4 < Math.max(0, it.requiredQ4 - it.consumedQ4);
                      return (
                        <tr key={it.id}>
                          <td>
                            {it.name}
                            {it.sku && <span className="text-muted small ms-2">{it.sku}</span>}
                          </td>
                          <td>{it.unit}</td>
                          <td className="text-end qty">{formatQty(it.qtyPerUnitQ4)}</td>
                          <td className="text-end">{it.lossPct4 ? formatPct(it.lossPct4) : "—"}</td>
                          <td className="text-end qty">{formatQty(it.requiredQ4)}</td>
                          <td className="text-end qty">{formatQty(it.consumedQ4)}</td>
                          <td className={`text-end qty${short ? " text-danger fw-semibold" : ""}`}>
                            {!it.stockControl || it.availableQ4 === null
                              ? "Sem controle"
                              : formatQty(it.availableQ4)}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
            <div className="box card p-3 mb-3">
              <h2 className="h6">Apontamentos</h2>
              {o.runs.length === 0 ? (
                <p className="text-muted small mb-0">
                  Nada produzido ainda. Cada apontamento baixa os insumos proporcionais e dá entrada no
                  produto.
                </p>
              ) : (
                <ol className="list-unstyled mb-0" aria-label="Apontamentos da produção">
                  {[...o.runs].reverse().map((r) => (
                    <li key={r.id} className="border-start border-3 ps-3 pb-3" data-testid="op-run">
                      <div className="d-flex flex-wrap align-items-center gap-2">
                        <strong>
                          Apontamento {r.number} · {formatQty(r.qtyQ4)} {o.product.unit}
                        </strong>
                        <span className="text-muted small">{formatDateBR(r.date)}</span>
                        {r.reversedAt ? (
                          <StatusBadge tone="secondary">Estornado</StatusBadge>
                        ) : (
                          <StatusBadge tone="success">{RUN_SOURCE_LABELS[r.source]}</StatusBadge>
                        )}
                        {r.costCents !== undefined && (
                          <span className="ms-auto fw-semibold">
                            {formatBRL(r.costCents)}
                            {r.unitCostCents !== undefined && (
                              <span className="text-muted small fw-normal">
                                {" "}
                                ({formatBRL(r.unitCostCents)}/un)
                              </span>
                            )}
                          </span>
                        )}
                      </div>
                      {r.lot && (
                        <div className="small">
                          Lote {r.lot.code}
                          {r.lot.expiresAt && ` · validade ${formatDateBR(r.lot.expiresAt)}`}
                        </div>
                      )}
                      <ul className="small mb-1">
                        {r.items.map((l) => (
                          <li key={l.componentId}>
                            {formatQty(l.qtyQ4)} × {l.name}
                            {l.costCents !== undefined && (
                              <span className="text-muted"> · {formatBRL(l.costCents)}</span>
                            )}
                          </li>
                        ))}
                      </ul>
                      {r.notes && <div className="small">{r.notes}</div>}
                      {r.reversedAt && (
                        <div className="small text-muted">
                          Estornado em {dateTimeBR(r.reversedAt)}
                          {r.reverseReason && ` — ${r.reverseReason}`}
                        </div>
                      )}
                      {canUpdate && canStock && !r.reversedAt && (
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-danger"
                          disabled={busy}
                          onClick={() => {
                            setReason("");
                            setReversing(r);
                          }}
                        >
                          Estornar apontamento {r.number}
                        </button>
                      )}
                    </li>
                  ))}
                </ol>
              )}
            </div>
            <div className="box card p-3 mb-3">
              <h2 className="h6">Histórico de situações</h2>
              <QueryStatus loading={history.isLoading} error={history.error} />
              {history.data && history.data.data.length === 0 && (
                <p className="text-muted small mb-0">Nenhuma mudança de situação ainda.</p>
              )}
              {history.data && history.data.data.length > 0 && (
                <div className="table-responsive">
                  <table className="table table-sm mb-0" aria-label="Histórico de situações">
                    <thead>
                      <tr>
                        <th scope="col">Quando</th>
                        <th scope="col">Mudança</th>
                        <th scope="col">Origem</th>
                        <th scope="col">Usuário</th>
                      </tr>
                    </thead>
                    <tbody>
                      {history.data.data.map((h) => (
                        <tr key={h.id}>
                          <td className="text-nowrap">{dateTimeBR(h.createdAt)}</td>
                          <td>{purchaseHistoryLabel(h)}</td>
                          <td>{h.automatic ? "Automática (apontamento)" : "Manual"}</td>
                          <td>{h.userName ?? "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
          <aside className="col-xl-3">
            <div className="box card p-3 mb-3">
              <h2 className="h6">Custo da produção</h2>
              {o.producedCostCents === undefined ? (
                <p className="text-muted small mb-0">Sem permissão para ver custos.</p>
              ) : (
                <dl className="row mb-0 small">
                  <dt className="col-7 fw-normal">Custo dos insumos</dt>
                  <dd className="col-5 text-end mb-1" data-testid="op-cost">
                    {formatBRL(o.producedCostCents)}
                  </dd>
                  <dt className="col-7 fw-normal">Custo unitário</dt>
                  <dd className="col-5 text-end mb-0" data-testid="op-unit-cost">
                    {formatBRL(o.unitCostCents ?? 0)}
                  </dd>
                </dl>
              )}
              {o.lockedReason && <p className="text-muted small mt-2 mb-0">{o.lockedReason}</p>}
            </div>
          </aside>
        </div>
      )}
      {o && dialog === "run" && (
        <RunDialog
          order={o}
          onClose={() => setDialog(null)}
          onDone={() => {
            setDialog(null);
            refresh();
          }}
        />
      )}
      {o && dialog === "finish" && (
        <FinishDialog
          order={o}
          onClose={() => setDialog(null)}
          onDone={() => {
            setDialog(null);
            refresh();
          }}
        />
      )}
      <ConfirmDialog
        open={!!canceling}
        title="Cancelar ordem de produção"
        message={`Cancelar a ordem nº ${o?.number ?? ""}? Os apontamentos são estornados (insumos voltam ao estoque e o produto sai).`}
        confirmLabel="Cancelar ordem"
        onConfirm={() => {
          if (canceling) status.mutate(canceling);
          setCanceling(null);
        }}
        onCancel={() => setCanceling(null)}
      />
      <Modal
        open={!!reversing}
        title={reversing ? `Estornar apontamento ${reversing.number}` : "Estornar apontamento"}
        onClose={() => setReversing(null)}
        footer={
          <>
            <button type="button" className="btn btn-outline-secondary" onClick={() => setReversing(null)}>
              Voltar
            </button>
            <button
              type="button"
              className="btn btn-danger"
              disabled={reverse.isPending}
              onClick={() => {
                if (reversing) reverse.mutate({ run: reversing, reason });
                setReversing(null);
              }}
            >
              Estornar
            </button>
          </>
        }
      >
        <p>
          O produto acabado sai do estoque e os insumos voltam (precisa haver saldo do acabado). A quantidade
          volta ao saldo da ordem.
        </p>
        <Field label="Motivo">
          <input value={reason} maxLength={500} data-autofocus onChange={(e) => setReason(e.target.value)} />
        </Field>
      </Modal>
    </>
  );
}

/** Lote do apontamento: obrigatório para produto com lotes quando a ordem não tem um. */
function useRunLot(order: ProductionOrderView) {
  const [lot, setLot] = useState(emptyLotDraft);
  const required = order.product.batchControl && !order.lot;
  const problem = order.product.batchControl ? lotDraftProblem(lot, required) : null;
  return { lot, setLot, required, problem, payload: order.product.batchControl ? lotPayload(lot) : null };
}

/** "Apontar produção": quantidade (padrão = saldo), data, lote e observação. */
function RunDialog({
  order,
  onClose,
  onDone,
}: {
  order: ProductionOrderView;
  onClose(): void;
  onDone(): void;
}) {
  const { api } = useSession();
  const toast = useToast();
  const [qtyQ4, setQty] = useState<number | null>(order.remainingQ4);
  const [date, setDate] = useState(todayInSaoPaulo());
  const [notes, setNotes] = useState("");
  const [tried, setTried] = useState(false);
  const lot = useRunLot(order);
  // Uma chave por diálogo: clique duplo ou repetição da rede não apontam duas vezes.
  const [idemKey] = useState(() => crypto.randomUUID());
  const problem =
    !qtyQ4 || qtyQ4 <= 0
      ? "Informe a quantidade produzida."
      : qtyQ4 > order.remainingQ4
        ? "A quantidade passa do saldo a produzir."
        : lot.problem;
  const save = useMutation({
    mutationFn: () =>
      api(`/production-orders/${order.id}/runs`, {
        method: "POST",
        headers: { "Idempotency-Key": idemKey },
        body: JSON.stringify({ qtyQ4, date, lot: lot.payload, notes: notes.trim() || null }),
      }),
    onSuccess: () => {
      toast("success", "Produção apontada");
      onDone();
    },
    onError: (e: Error) => toast("danger", errorMessage(e)),
  });
  return (
    <Modal
      open
      title={`Apontar produção da ordem nº ${order.number}`}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn btn-outline-secondary" onClick={onClose}>
            Voltar
          </button>
          <button
            type="button"
            className="btn btn-primary"
            disabled={save.isPending}
            onClick={() => {
              setTried(true);
              if (!problem) save.mutate();
            }}
          >
            {save.isPending ? "Apontando…" : "Apontar"}
          </button>
        </>
      }
    >
      <p className="small text-muted">
        Saldo a produzir: {formatQty(order.remainingQ4)} {order.product.unit}. Os insumos saem de{" "}
        {order.sourceWarehouse.name} e o produto entra em {order.targetWarehouse.name}.
      </p>
      <div className="row">
        <Field label="Quantidade produzida" required className="col-sm-6">
          <QtyInput value={qtyQ4} onChange={setQty} data-autofocus />
        </Field>
        <Field label="Data" className="col-sm-6">
          <input type="date" value={date} max={todayInSaoPaulo()} onChange={(e) => setDate(e.target.value)} />
        </Field>
      </div>
      {order.product.batchControl && (
        <>
          {order.lot && <p className="small mb-1">Em branco, usa o lote da ordem ({order.lot.code}).</p>}
          <LotFields value={lot.lot} onChange={lot.setLot} required={lot.required} />
        </>
      )}
      <Field label="Observação" className="col-12">
        <input value={notes} maxLength={500} onChange={(e) => setNotes(e.target.value)} />
      </Field>
      {tried && problem && (
        <p className="text-danger small mb-0" role="alert">
          {problem}
        </p>
      )}
    </Modal>
  );
}

/** "Finalizar": produzir o saldo (apontamento do que falta) ou encerrar com o que já foi produzido. */
function FinishDialog({
  order,
  onClose,
  onDone,
}: {
  order: ProductionOrderView;
  onClose(): void;
  onDone(): void;
}) {
  const { api } = useSession();
  const toast = useToast();
  const [mode, setMode] = useState<"produce_remaining" | "close">(
    order.producedQ4 > 0 ? "close" : "produce_remaining",
  );
  const [date, setDate] = useState(todayInSaoPaulo());
  const [tried, setTried] = useState(false);
  const lot = useRunLot(order);
  const [idemKey] = useState(() => crypto.randomUUID());
  const problem =
    mode === "close" && order.producedQ4 === 0
      ? "Nada foi produzido: aponte a produção ou cancele a ordem."
      : mode === "produce_remaining"
        ? lot.problem
        : null;
  const save = useMutation({
    mutationFn: () =>
      api(`/production-orders/${order.id}/finish`, {
        method: "POST",
        headers: { "Idempotency-Key": idemKey },
        body: JSON.stringify({ mode, date, lot: mode === "produce_remaining" ? lot.payload : null }),
      }),
    onSuccess: () => {
      toast("success", "Ordem de produção finalizada");
      onDone();
    },
    onError: (e: Error) => toast("danger", errorMessage(e)),
  });
  const radio = (value: typeof mode, label: string, help: string) => (
    <div className="form-check mb-2">
      <input
        id={`op-finish-${value}`}
        type="radio"
        name="op-finish"
        className="form-check-input"
        checked={mode === value}
        onChange={() => setMode(value)}
      />
      <label className="form-check-label" htmlFor={`op-finish-${value}`}>
        {label}
        <span className="d-block small text-muted">{help}</span>
      </label>
    </div>
  );
  return (
    <Modal
      open
      title={`Finalizar ordem de produção nº ${order.number}`}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn btn-outline-secondary" onClick={onClose}>
            Voltar
          </button>
          <button
            type="button"
            className="btn btn-primary"
            disabled={save.isPending}
            onClick={() => {
              setTried(true);
              if (!problem) save.mutate();
            }}
          >
            Finalizar
          </button>
        </>
      }
    >
      {radio(
        "produce_remaining",
        `Produzir o saldo (${formatQty(order.remainingQ4)} ${order.product.unit})`,
        "Aponta o que falta: baixa os insumos e dá entrada no produto.",
      )}
      {radio(
        "close",
        `Encerrar com o que foi produzido (${formatQty(order.producedQ4)} ${order.product.unit})`,
        "O saldo deixa de ser produzido. Para produzir depois, volte a situação da ordem.",
      )}
      {mode === "produce_remaining" && (
        <>
          <Field label="Data" className="col-sm-6">
            <input
              type="date"
              value={date}
              max={todayInSaoPaulo()}
              onChange={(e) => setDate(e.target.value)}
            />
          </Field>
          {order.product.batchControl && (
            <LotFields value={lot.lot} onChange={lot.setLot} required={lot.required} />
          )}
        </>
      )}
      {tried && problem && (
        <p className="text-danger small mb-0" role="alert">
          {problem}
        </p>
      )}
    </Modal>
  );
}

function Info({ label, children }: { label: string; children: ReactNode }) {
  return (
    <>
      <dt className="col-sm-4 col-lg-3 fw-normal text-muted">{label}</dt>
      <dd className="col-sm-8 col-lg-9">{children || "—"}</dd>
    </>
  );
}
